/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { Observable, Observer } from "rxjs";
import { AxiosSubscriber } from "./AxiosSubscriber";

import { HTTP_METHOD } from "@nf/constants";
import {
  ICanceledSubscriptionModel,
  IConsentListModel,
  ICustomerModel,
  ISubscriptionListModel,
  IUserConsentModel,
  ISubscriptionSwitchResponseModel,
  IConsentModel,
  ICancellationSubscriptionReasons,
  ICancelSubscriptionSwitchResponseModel,
  ICancelSubscriptionRequestModel,
  IOfferSwitchResponseModel,
  ISubscriptionSwitchRequestModel,
  IUncancelSubscriptionResponseModel,
} from "../models";

export class UserService {
  get url(): string {
    return "/v1";
  }

  private getMethod<T>(url: string) {
    return new Observable(
      (observer: Observer<T>) =>
        new AxiosSubscriber(observer, {
          method: HTTP_METHOD.GET,
          url: `${this.url}/${url}`,
        })
    );
  }

  private putMethod<T, P>(url: string, data: P) {
    return new Observable(
      (observer: Observer<T>) =>
        new AxiosSubscriber(observer, {
          data,
          method: HTTP_METHOD.PUT,
          url: `${this.url}/${url}`,
        })
    );
  }

  private deleteMethod<T, P>(url: string, data: P) {
    return new Observable(
      (observer: Observer<T>) =>
        new AxiosSubscriber(observer, {
          method: HTTP_METHOD.DELETE,
          url: `${this.url}/${url}`,
          data,
        })
    );
  }

  public availableConsents = (): Observable<
    IConsentListModel<IConsentModel[]>
  > => this.getMethod<IConsentListModel<IConsentModel[]>>("available-consents");

  public consents = (): Observable<IConsentListModel<IUserConsentModel[]>> =>
    this.getMethod<IConsentListModel<IUserConsentModel[]>>("consents");

  public updateUserConsents = (
    data: IConsentListModel<Partial<IConsentModel>[]>
  ): Observable<void> =>
    this.putMethod<void, IConsentListModel<Partial<IConsentModel>[]>>(
      "consents",
      data
    );

  public profile = (): Observable<ICustomerModel> =>
    this.getMethod<ICustomerModel>("customer");

  public updateProfile = (
    data: Partial<ICustomerModel>
  ): Observable<ICustomerModel> =>
    this.putMethod<ICustomerModel, Partial<ICustomerModel>>("customer", data);

  public updateEmail = (email: string): Observable<ICustomerModel> =>
    new Observable(
      (observer: Observer<ICustomerModel>) =>
        new AxiosSubscriber(observer, {
          data: { email },
          method: HTTP_METHOD.PATCH,
          url: `${this.url}/email`,
        })
    );

  public subscriptions = (): Observable<ISubscriptionListModel> =>
    new Observable(
      (observer: Observer<ISubscriptionListModel>) =>
        new AxiosSubscriber(observer, {
          method: HTTP_METHOD.GET,
          url: `/v2/subscriptions`,
        })
    );

  public cancelSubscription = (
    data: ICancelSubscriptionRequestModel
  ): Observable<ICanceledSubscriptionModel> =>
    this.deleteMethod<
      ICanceledSubscriptionModel,
      | {
          cancellation_answers_v2: string[];
          cancellation_reasons_v2: ICancellationSubscriptionReasons;
        }
      | undefined
    >(`subscriptions/${data.id}`, data.reason);

  public uncancelSubscription = (
    id: number
  ): Observable<IUncancelSubscriptionResponseModel> =>
    new Observable(
      (observer: Observer<IUncancelSubscriptionResponseModel>) =>
        new AxiosSubscriber(observer, {
          method: HTTP_METHOD.PATCH,
          url: `${this.url}/subscriptions/${id}/un-cancel`,
        })
    );

  public offerSwitches = (): Observable<IOfferSwitchResponseModel[]> =>
    new Observable(
      (observer: Observer<IOfferSwitchResponseModel[]>) =>
        new AxiosSubscriber(observer, {
          method: HTTP_METHOD.GET,
          url: `${this.url}/offer-switches`,
        })
    );

  public switchSubscription = (
    data: ISubscriptionSwitchRequestModel
  ): Observable<ISubscriptionSwitchResponseModel> =>
    new Observable(
      (observer: Observer<ISubscriptionSwitchResponseModel>) =>
        new AxiosSubscriber(observer, {
          method: HTTP_METHOD.POST,
          url: `${this.url}/subscription-switches`,
          data,
        })
    );

  public cancelSubscriptionSwitch =
    (): Observable<ICancelSubscriptionSwitchResponseModel> =>
      new Observable(
        (observer: Observer<ICancelSubscriptionSwitchResponseModel>) =>
          new AxiosSubscriber(observer, {
            method: HTTP_METHOD.DELETE,
            url: `${this.url}/subscription-switches`,
          })
      );
}
